import React from 'react';
import { Currency } from 'api/account';
import { UserMessage } from 'api/conversations';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { Role, User } from 'api/models';
import { FieldValue, FieldValueType } from 'api/tasks';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import {
  GroupCriteria,
  GroupedFilter,
} from 'components/organisms/tasks/grouped-filter-popover';

export type ResourceRequestCommentType = Pick<
  UserMessage,
  'messageType' | 'private' | 'commentMeta' | 'user' | 'content'
>;

export const ResourceRequestTypeMap = {
  NewTeamMember: 'NEW_TEAM_MEMBER',
  Placeholder: 'PLACEHOLDER',
  Extension: 'EXTENSION',
  Replacement: 'REPLACEMENT',
  DeleteAllocation: 'DELETE',
  ShiftAllocation: 'SHIFT',
} as const;

export type ResourceRequestType =
  typeof ResourceRequestTypeMap[keyof typeof ResourceRequestTypeMap];

export const RequestStatusMap = {
  Todo: 'TO_DO',
  Approved: 'APPROVED',
  Blocked: 'BLOCKED',
} as const;

export type RequestStatus =
  typeof RequestStatusMap[keyof typeof RequestStatusMap];

export const AllocationUnit = {
  Week: 'HOURS_PER_WEEK',
  Total: 'TOTAL',
} as const;

export interface ResourceRequestReplaceMentEntity {
  userId?: number;
  teamId?: number;
  roleId?: number;
}

export interface Meta {
  teamMemberFields?: SimpleSearchFilter;
  assignPlaceholder?: boolean;
  userOverride?: boolean;

  /* Replace Request related fields start */
  replacingEntity?: ResourceRequestReplaceMentEntity;
  replaceWithEntity?: ResourceRequestReplaceMentEntity;
  entireAllocation?: boolean;
  /* Replace Request related fields end */

  // shift allocation related fields
  noOfDays?: number;
  entireProject?: boolean;
  entireTeam?: boolean;
  targetStartDate?: string;
}

const AllocationMediumMap = {
  Hard: 'HARD',
  Soft: 'SOFT',
} as const;

const AllocationTypeMap = {
  User: 'USER',
  Placeholder: 'PLACEHOLDER',
} as const;

const resourceRequestCapacityTypeMap = {
  PerWeek: 'HOURS_PER_WEEK',
  Total: 'TOTAL',
  PerDay: 'HOURS_PER_DAY',
} as const;

export type ResourceRequestCapacityType =
  typeof resourceRequestCapacityTypeMap[keyof typeof resourceRequestCapacityTypeMap];

export type AllocationType =
  typeof AllocationTypeMap[keyof typeof AllocationTypeMap];

export type AllocationMediumType =
  typeof AllocationMediumMap[keyof typeof AllocationMediumMap];

const RequestedBy = {
  All: 'all',
  Me: 'me',
} as const;

export type RequestStatusType =
  typeof RequestStatusMap[keyof typeof RequestStatusMap];

export type RequestedByType = typeof RequestedBy[keyof typeof RequestedBy];

export type AllocationUnitType =
  typeof AllocationUnit[keyof typeof AllocationUnit];

export interface ResourceRequestTeamMember
  extends Pick<User, 'userId' | 'firstName' | 'lastName' | 'photoUrl'> {
  userId: number;
  identityType: 'USER';
  emailId?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  photoUrl?: string;
  referenceTeamId?: number;
  role?: {
    roleId: number;
  };
}
export interface Team {
  teamId: number;
  identityType: 'TEAM';
  teamName?: string;
  roleId?: number;
}

export interface ResourceRequest {
  resourceRequestId?: number;
  startDate?: string;
  dueDate?: string;
  allocationType?: AllocationType;
  allocationMedium?: AllocationMediumType;
  resourceRequestCapacityType?: ResourceRequestCapacityType;
  totalMins?: number;
  teamMembers?: ResourceRequestTeamMember[];
  teams?: Team[];
  meta?: Meta;
  message?: Record<string, unknown>;
  allocatedSeconds?: number;
  roleId?: number;
  roles?: number[];
}

export interface ResourceApproval {
  resourceRequestId?: number;
  startDate?: string;
  dueDate?: string;
  allocationType?: AllocationType;
  allocationMedium?: AllocationMediumType;
  resourceRequestCapacityType?: ResourceRequestCapacityType;
  totalMins?: number;
  teamMembers?: ResourceRequestTeamMember[];
  teams?: Team[];
  meta?: Meta;
  roleId?: number;
  roles?: number[];
  allocatedSeconds?: number;
}

export interface ApprovalFilterPayload {
  resourceApproval: Omit<ResourceApproval, 'roleId'> & {
    roles?: number[];
  };
}

export interface ResourceRequestWrapper {
  resourceRequestMappingId: number;
  projectId: number;
  project?: {
    projectName?: string;
    customer?: {
      companyId?: number;
      companyName?: string;
    };
  };
  accountId: number;
  status: RequestStatus;
  resourceRequestType: ResourceRequestType;
  requestedBy: number;
  requestedOn: string;
  resourceRequest?: ResourceRequest;
  resourceApproval?: ResourceApproval;
  resolvedBy?: number;
  resolvedAt?: string;
  assignedTo?: {
    userId: number;
  };
  ignoreSoftAllocation?: boolean;
}

export interface ResoueceRequestPaginatedParams {
  startRow: number;
  endRow: number;
  filter?: GroupCriteria[];
  match?: QueryCombinerType;
  sort?: string;
  source?: ResourceRequestSourceType;
  groupBy?: string;
  search?: string;
}

export interface RequestApprovalFilterApiData {
  id: number;
  forecastedUtilization?: number;
  diversifiedRevenue?: number;
  overAllocation?: number;
  sourceType: 'USER' | 'TEAM';
  name?: string;
  roleId?: number;
  fields?: FieldValue<FieldValueType>[];
  projectMember?: boolean;
  photoUrl?: string;
  fieldName?: string;
  fieldDiff?: Record<number, boolean>;
  referenceTeamId?: number;
  costRate?: number;
  margin?: number;
  projectCurrency?: Currency;
}

export interface ApprovalGridTransformedData
  extends RequestApprovalFilterApiData {
  role: Role;
}

export interface CreateResourceRequestInputType {
  projectId: number;
  status: RequestStatus;
  resourceRequestType: ResourceRequestType;
  requestedBy: number;
  resourceRequest: Omit<ResourceRequest, 'resourceRequestId'>;
  comment?: ResourceRequestCommentType;
}

export interface UpdateResourceRequestStatusInputType {
  resourceRequestType: ResourceRequestType;
  resourceApproval: ResourceApproval;
  comment?: ResourceRequestCommentType;
  status: RequestStatus;
  projectId?: number;
}

export interface UpdateResourceRequestInputType
  extends CreateResourceRequestInputType {
  resourceRequestMappingId: number;
}

interface ResourceAllocationMetaData {
  userId: number;
  projectId: number;
  minStartDate: string;
  maxEndDate: string;
}
export interface ResourceAllocationMeta {
  users?: Record<number, Array<ResourceAllocationMetaData>>;
  teams?: Record<number, Array<ResourceAllocationMetaData>>;
}

export interface ResourceApprovalFilteredDataPaginated {
  data: RequestApprovalFilterApiData[];
  projectHours: ResourceApprovalFilteredDataProjectHours;
  projectBudget: ResourceApprovalFilteredDataProjectBudget;
  count: number;
  canApprove?: boolean;
}

export interface ResourceApprovalFilteredDataProjectHours {
  budgetedHours: number;
  allocateHours: number;
  extraHours: number;
}

export interface ResourceApprovalFilteredDataProjectBudget {
  projectBudget: number;
  estimatedRevenue: number;
}

export interface RequestFilterProps {
  setFilterData: React.Dispatch<
    React.SetStateAction<GroupedFilter | undefined>
  >;
  filterData?: GroupedFilter;
}

export interface OverAllocationResponseType {
  overAllocation: number;
}

export type ResourceRequestSourceType = 'approval' | 'request';

export interface ResourceRequestLastSeenAtResponse {
  lastSeenAt: string;
}

export interface ResourceRequestBase {
  projectId: number;
  resourceRequestMappingId: number;
}

export interface ResourceRequestUpdateLastSeenAtPayload
  extends ResourceRequestBase {
  lastSeenAt: string;
}

export type ResourceRequestGetLastSeenAtQueryParams = ResourceRequestBase;

export type ShiftAllocationStartScope = 'ENTIRE_PROJECT' | 'SPECIFIC_DATE';
export type ShiftAllocationEndScopeShiftTo =
  | 'SPECIFIC_DATE'
  | 'NO_OF_WORKING_DAYS';
export type ShiftAllocationTeamMembersScope =
  | 'SPECIFIC_TEAM_MEMBERS'
  | 'ALL_TEAM_MEMBERS';
